import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BookOpen, 
  Zap, 
  FileText, 
  Settings, 
  Users, 
  CreditCard,
  ArrowLeft,
  ExternalLink,
  Search,
  ChevronRight,
  Brain,
  Briefcase,
  FileCheck,
  Upload,
  UserCheck,
  Bell,
  Mail,
  Download
} from "lucide-react";
import Logo from "../components/Logo";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const docSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Zap,
    description: "Set up your first intake form in minutes",
    articles: [
      { title: "Creating your account", time: "2 min", slug: "creating-your-account" },
      { title: "Setting up your first form", time: "3 min", slug: "setting-up-your-first-form" },
      { title: "Choosing pre-booking vs post-booking mode", time: "4 min", slug: "choosing-pre-booking-vs-post-booking" },
      { title: "Customizing form fields", time: "5 min", slug: "customizing-form-fields" },
      { title: "Sharing your intake form", time: "2 min", slug: "sharing-your-intake-form" }
    ]
  },
  {
    id: "crm-pipeline",
    title: "CRM & Pipeline",
    icon: Briefcase,
    description: "Track and manage leads through your sales process",
    articles: [
      { title: "Understanding the pipeline view", time: "4 min", slug: "understanding-the-pipeline-view" },
      { title: "Lead status workflow (Pre-booking)", time: "3 min", slug: "lead-status-workflow-pre-booking" },
      { title: "Intake status workflow (Post-booking)", time: "4 min", slug: "intake-status-workflow-post-booking" },
      { title: "Activity Timeline & audit trail", time: "3 min", slug: "activity-timeline-audit-trail" },
      { title: "Adding notes and internal comments", time: "2 min", slug: "adding-notes-and-comments" },
      { title: "Using tags to organize leads", time: "2 min", slug: "using-tags-to-organize" },
      { title: "Setting and managing reminders", time: "3 min", slug: "setting-and-managing-reminders" }
    ]
  },
  {
    id: "ai-scoring",
    title: "AI Lead Scoring",
    icon: Brain,
    description: "Understand how AI analyzes and prioritizes submissions",
    articles: [
      { title: "How lead scoring works (0-100)", time: "4 min", slug: "how-lead-scoring-works" },
      { title: "Understanding priority labels", time: "2 min", slug: "understanding-priority-labels" },
      { title: "AI-generated summaries explained", time: "3 min", slug: "ai-generated-summaries" },
      { title: "Customizing AI scoring weights", time: "3 min", slug: "customizing-ai-scoring-weights" },
      { title: "AI Form Builder: Generate forms with AI", time: "4 min", slug: "ai-form-builder" },
      { title: "Editing AI-generated fields before creation", time: "3 min", slug: "editing-ai-generated-fields" },
      { title: "Intake clarity scoring (Post-booking)", time: "4 min", slug: "intake-clarity-scoring" },
      { title: "Risk flags and follow-up suggestions", time: "3 min", slug: "risk-flags-and-followups" }
    ]
  },
  {
    id: "agreements",
    title: "Agreement Acknowledgment",
    icon: FileCheck,
    description: "Require clients to review and sign your terms",
    articles: [
      { title: "Setting up agreement links", time: "3 min", slug: "setting-up-agreement-links" },
      { title: "Adding agreement instructions", time: "2 min", slug: "adding-agreement-instructions" },
      { title: "Enabling per-form agreement requirement", time: "2 min", slug: "enabling-per-form-agreement" },
      { title: "Viewing agreement status per submission", time: "2 min", slug: "viewing-agreement-status" },
      { title: "Exporting agreement data", time: "2 min", slug: "exporting-agreement-data" }
    ]
  },
  {
    id: "documents",
    title: "Document Collection",
    icon: Upload,
    description: "Request and collect files from clients",
    articles: [
      { title: "Setting up document upload links", time: "3 min", slug: "setting-up-document-upload-links" },
      { title: "Using Dropbox file requests", time: "3 min", slug: "using-dropbox-file-requests" },
      { title: "Using Google Drive for uploads", time: "3 min", slug: "using-google-drive-for-uploads" },
      { title: "Adding upload instructions for clients", time: "2 min", slug: "adding-upload-instructions" }
    ]
  },
  {
    id: "client-onboarding",
    title: "Client Onboarding",
    icon: UserCheck,
    description: "Post-booking intake for existing clients",
    articles: [
      { title: "When to use post-booking mode", time: "3 min", slug: "when-to-use-post-booking-mode" },
      { title: "Creating a post-booking intake form", time: "4 min", slug: "creating-post-booking-intake-form" },
      { title: "Sending intake requests to clients", time: "2 min", slug: "sending-intake-requests-to-clients" },
      { title: "Managing incomplete intakes", time: "3 min", slug: "managing-incomplete-intakes" },
      { title: "Intake reminder automations", time: "3 min", slug: "intake-reminder-automations" }
    ]
  },
  {
    id: "notifications",
    title: "Automations & Notifications",
    icon: Bell,
    description: "Auto Flows, email templates, and notification settings",
    articles: [
      { title: "Understanding Auto Flows (preset automations)", time: "4 min", slug: "understanding-auto-flows" },
      { title: "Default automations for new accounts", time: "3 min", slug: "default-automations" },
      { title: "Editing automation email templates", time: "3 min", slug: "editing-automation-email-templates" },
      { title: "Creating custom automations", time: "4 min", slug: "creating-custom-automations" },
      { title: "Lifecycle mapping (status → automation)", time: "3 min", slug: "lifecycle-mapping" },
      { title: "Email notification settings", time: "2 min", slug: "email-notification-settings" },
      { title: "New submission alerts", time: "2 min", slug: "new-submission-alerts" },
      { title: "Setting up reminders", time: "3 min", slug: "setting-up-reminders" },
      { title: "Automated intake reminders", time: "3 min", slug: "automated-intake-reminders" }
    ]
  },
  {
    id: "billing",
    title: "Billing & Plans",
    icon: CreditCard,
    description: "Manage your subscription and billing",
    articles: [
      { title: "Understanding your trial", time: "2 min", slug: "understanding-your-trial" },
      { title: "Plan comparison (Starter vs Pro)", time: "3 min", slug: "plan-comparison" },
      { title: "Upgrading your plan", time: "2 min", slug: "upgrading-your-plan" },
      { title: "Managing payment methods", time: "2 min", slug: "managing-payment-methods" },
      { title: "Canceling your subscription", time: "2 min", slug: "canceling-your-subscription" }
    ]
  },
  {
    id: "settings",
    title: "Account Settings",
    icon: Settings,
    description: "Configure your account, branding, and integrations",
    articles: [
      { title: "Updating business info", time: "2 min", slug: "updating-business-info" },
      { title: "Custom branding (colors & logo)", time: "3 min", slug: "custom-branding" },
      { title: "Adding scheduling links", time: "3 min", slug: "adding-scheduling-links" },
      { title: "E-signature setup (SignWell)", time: "3 min", slug: "e-signature-setup" },
      { title: "Configuring agreement settings", time: "3 min", slug: "configuring-agreement-settings" },
      { title: "Consent checkbox for lead forms", time: "2 min", slug: "consent-checkbox" },
      { title: "Guided setup walkthrough", time: "3 min", slug: "guided-setup" },
      { title: "Document handling options", time: "3 min", slug: "document-handling-options" }
    ]
  }
];

export default function DocsPage() {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <MarketingNav />
      
      {/* Header */}
      <section className="pt-32 pb-12 px-6 bg-white border-b border-[#e5e5e0]">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">Documentation</h1>
          </div>
          <p className="text-lg text-muted-foreground mb-8">
            Everything you need to get started with HeyIntake. Self-serve guides for all plans.
          </p>
          
          {/* Search placeholder */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input 
              type="text"
              placeholder="Search documentation..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#e5e5e0] bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              disabled
            />
          </div>
        </div>
      </section>
      
      {/* Quick Links */}
      <section className="py-8 px-6 bg-[#f8f8f5] border-b border-[#e5e5e0]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-3">
            <span className="text-sm text-muted-foreground">Quick links:</span>
            <a href="#getting-started" className="text-sm text-primary hover:underline">Getting Started</a>
            <span className="text-muted-foreground">•</span>
            <a href="#crm-pipeline" className="text-sm text-primary hover:underline">CRM & Pipeline</a>
            <span className="text-muted-foreground">•</span>
            <a href="#ai-scoring" className="text-sm text-primary hover:underline">AI Scoring & Form Builder</a>
            <span className="text-muted-foreground">•</span>
            <a href="#notifications" className="text-sm text-primary hover:underline">Automations</a>
            <span className="text-muted-foreground">•</span>
            <a href="#agreements" className="text-sm text-primary hover:underline">Agreements</a>
            <span className="text-muted-foreground">•</span>
            <a href="#settings" className="text-sm text-primary hover:underline">Settings & Branding</a>
            <span className="text-muted-foreground">•</span>
            <a href="#billing" className="text-sm text-primary hover:underline">Billing</a>
          </div>
        </div>
      </section>
      
      {/* Documentation Sections */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {docSections.map((section) => (
              <Card key={section.id} id={section.id} className="bg-white border-[#e5e5e0]">
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                      <section.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{section.title}</CardTitle>
                      <p className="text-muted-foreground mt-1">{section.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {section.articles.map((article, idx) => (
                      <li key={idx}>
                        <Link 
                          to={article.slug ? `/docs/${article.slug}` : '#'}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-[#f8f8f5] transition-colors group"
                        >
                          <span className="text-foreground group-hover:text-primary transition-colors">
                            {article.title}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{article.time} read</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Support Notice */}
          <Card className="mt-12 bg-[#f0fdf4] border-[#86efac]">
            <CardContent className="py-6">
              <h3 className="font-semibold text-foreground mb-2">Need More Help?</h3>
              <p className="text-muted-foreground mb-4">
                Our documentation covers most common questions. Support options vary by plan:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="font-medium text-foreground">Starter:</span>
                  <span className="text-muted-foreground">Self-serve documentation only</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-medium text-foreground">Professional:</span>
                  <span className="text-muted-foreground">Email support (24-48hr response)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-medium text-foreground">Enterprise:</span>
                  <span className="text-muted-foreground">Dedicated support + phone access</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-[#86efac]">
                <a href="mailto:support@heyintake.com" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                  Contact Support <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <MarketingFooter />
    </div>
  );
}
